import axios from 'axios';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Backend integration point: Replace baseURL with environment variable
const axiosInstance = axios?.create({
  baseURL: '/api/proxy',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

axiosInstance?.interceptors?.request?.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('accessToken');
      // Only set the Authorization header if it is not already explicitly set
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance?.interceptors?.response?.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if the error response is 401 and the request hasn't been retried yet
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (typeof window === 'undefined') {
        return Promise.reject(error);
      }

      const handleSessionExpired = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('expiresIn');
        localStorage.removeItem('tokenType');
        localStorage.removeItem('userPhone');
        localStorage.removeItem('idle_is_locked');
        localStorage.removeItem('isLocked');
        localStorage.removeItem('idle_last_active_time');
        window.location.href = '/';
      };

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token available, logout user
        handleSessionExpired();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call the refresh API. We use basic axios to bypass interceptors
        const response = await axios.post(
          '/api/proxy/auth/refresh',
          {},
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const { accessToken, refreshToken: newRefreshToken, expiresIn, tokenType } = response.data;

        // Save new tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('expiresIn', String(expiresIn));
        localStorage.setItem('tokenType', tokenType || 'Bearer');
        localStorage.removeItem('idle_is_locked');
        localStorage.removeItem('isLocked');
        localStorage.setItem('idle_last_active_time', Date.now().toString());

        isRefreshing = false;
        processQueue(null, accessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // If refresh fails, log out the user
        handleSessionExpired();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;